/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table } from "@tanstack/react-table";
import { debounce } from "lodash";
import { X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import {
  EngineTypeRequestEnum,
  EngineTypeSolicitationEnum,
} from "@/enum/engine-history-type";
import { Label } from "components/ui/label";
import { useFilterEngineAction } from "../duck/hook/action";
import { useFilterEngineHistoryState } from "../duck/hook/state";
import SelectFilters from "./select-filters";
import { ModernCard } from "./modern-card";

const typeRequestMap: Record<EngineTypeRequestEnum, string> = {
  CREATE: "Criação",
  UPDATE: "Alteração",
  DELETE: "Exclusão",
};

const typeSolicitationMap: Record<EngineTypeSolicitationEnum, string> = {
  BC: "Base de Cálculo",
  EXCEPTION: "Exceção",
};

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

type FilterKeys =
  | "search"
  | "type_request"
  | "type_solicitation"
  | "created_at"
  | "meta";

export function DataTableToolbar<TData>({}: DataTableToolbarProps<TData>) {
  const { updateFilter, clearFilters } = useFilterEngineAction();
  const filterState = useFilterEngineHistoryState();

  const [searchValue, setSearchValue] = useState(
    filterState.filters.search || ""
  );

  const debouncedSearchUpdate = useCallback(
    debounce((value: string) => {
      updateFilter("search", value);
    }, 500),
    []
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    debouncedSearchUpdate(event.target.value);
  };

  useEffect(() => {
    setSearchValue(filterState.filters.search || "");
  }, [filterState.filters.search]);

  const handleFilterChange = (key: FilterKeys, value: string) => {
    updateFilter(key, value === "all" ? undefined : value);
  };

  return (
    <>
      <ModernCard title="Filtros de Pesquisa" description="" className="w-full">
        <div className="flex flex-col gap-6 mb-5">
          <div className="flex flex-wrap items-end gap-6">
            <div className="flex flex-col w-56">
              <Label
                htmlFor="search-input"
                className="text-sm font-semibold text-gray-700 mb-1"
              >
                Responsável
              </Label>
              <Input
                id="search-input"
                placeholder="Digite o nome do responsável..."
                value={searchValue}
                onChange={handleSearchChange}
                className="w-full"
              />
            </div>

            <div className="flex flex-col w-56">
              <Label
                htmlFor="type-filter"
                className="text-sm font-semibold text-gray-700 mb-1"
              >
                Tipo de Solicitação
              </Label>
              <Select
                onValueChange={(value) =>
                  handleFilterChange("type_solicitation", value)
                }
                value={filterState.filters.type_solicitation || "all"}
              >
                <SelectTrigger id="type-filter" className="w-full">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(typeSolicitationMap).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col w-56">
              <Label
                htmlFor="tax-type-filter"
                className="text-sm font-semibold text-gray-700 mb-1"
              >
                Tipo de Requisição
              </Label>
              <Select
                onValueChange={(value) =>
                  handleFilterChange("type_request", value)
                }
                value={filterState.filters.type_request || "all"}
              >
                <SelectTrigger id="tax-type-filter" className="w-full">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(typeRequestMap).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-6">
            <div className="flex flex-col w-56">
              <Label
                htmlFor="created-at"
                className="text-sm font-semibold text-gray-700 mb-1"
              >
                Criado em
              </Label>
              <SelectFilters />
            </div>
          </div>
        </div>

        <div className="border-t-2 border-gray-200">
          {Object.keys(filterState.filters).some(
            (k) => filterState.filters[k as keyof typeof filterState.filters]
          ) && (
            <Button
              variant="default"
              onClick={clearFilters}
              className="h-10 mt-5 px-4 py-2 border rounded-xl shadow-sm border-transparent bg-transparent hover:bg-[#F5CB2B]"
            >
              <X className="w-5 h-5 text-gray-700" />
              <span className="text-gray-700 font-semibold text-sm">
                Resetar Filtros
              </span>
            </Button>
          )}
        </div>
      </ModernCard>
    </>
  );
}
